/**
 * Hand-drawn SVG geometry. Every shape is seeded from a string, so a figure
 * draws the same way on the server and on every visit: the wobble is part
 * of the design, not noise.
 */

export type Point = readonly [number, number];
export type Random = () => number;

function hashSeed(seed: string): number {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** mulberry32: small, fast and stable across runtimes. */
export function seeded(seed: string): Random {
  let state = hashSeed(seed);
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const jitter = (random: Random, amount: number) => (random() * 2 - 1) * amount;
const fmt = (value: number) => Number(value.toFixed(1));

/** One pen stroke between two points: slightly bowed, ends a little off. */
function stroke(from: Point, to: Point, random: Random, roughness: number): string {
  const [x1, y1] = from;
  const [x2, y2] = to;
  const length = Math.hypot(x2 - x1, y2 - y1);
  const wobble = Math.min(2.4, 0.5 + length * 0.012) * roughness;
  const sx = x1 + jitter(random, wobble);
  const sy = y1 + jitter(random, wobble);
  const ex = x2 + jitter(random, wobble);
  const ey = y2 + jitter(random, wobble);
  // Bow the stroke perpendicular to its direction, as a hand does.
  const bow = jitter(random, Math.min(3, length * 0.018) * roughness);
  const nx = length ? -(y2 - y1) / length : 0;
  const ny = length ? (x2 - x1) / length : 0;
  const t = 0.35 + random() * 0.3;
  const cx = sx + (ex - sx) * t + nx * bow;
  const cy = sy + (ey - sy) * t + ny * bow;
  return `M${fmt(sx)} ${fmt(sy)}Q${fmt(cx)} ${fmt(cy)} ${fmt(ex)} ${fmt(ey)}`;
}

/** A line drawn twice, like a pen going back over it. */
export function roughLine(from: Point, to: Point, random: Random, roughness = 1): string {
  return stroke(from, to, random, roughness) + stroke(from, to, random, roughness * 0.7);
}

/** A rectangle whose edges overshoot their corners. */
export function roughRect(x: number, y: number, width: number, height: number, random: Random): string {
  const over = () => 1 + random() * 3;
  return [
    roughLine([x - over(), y], [x + width + over(), y], random),
    roughLine([x + width, y - over()], [x + width, y + height + over()], random),
    roughLine([x + width + over(), y + height], [x - over(), y + height], random),
    roughLine([x, y + height + over()], [x, y - over()], random),
  ].join("");
}

/**
 * 45° hatching clipped to a rectangle. Strokes stop a little short of the
 * edges at random, as quick hatching does.
 */
export function hachure(
  x: number,
  y: number,
  width: number,
  height: number,
  random: Random,
  gap = 9,
): string {
  const paths: string[] = [];
  for (let offset = -height + gap / 2; offset < width; offset += gap) {
    // Points on this stroke are (x + offset + t, y + height - t).
    const start = Math.max(0, -offset);
    const end = Math.min(height, width - offset);
    if (end - start < 4) continue;
    const trim = () => random() * 2.5;
    const t0 = start + trim();
    const t1 = end - trim();
    paths.push(
      stroke(
        [x + offset + t0, y + height - t0],
        [x + offset + t1, y + height - t1],
        random,
        0.6,
      ),
    );
  }
  return paths.join("");
}

/** Catmull-Rom points to a smooth cubic path. */
function smoothPath(points: Point[]): string {
  const [first] = points;
  if (!first) return "";
  let path = `M${fmt(first[0])} ${fmt(first[1])}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const p0 = points[Math.max(0, index - 1)]!;
    const p1 = points[index]!;
    const p2 = points[index + 1]!;
    const p3 = points[Math.min(points.length - 1, index + 2)]!;
    const c1: Point = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2: Point = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    path += `C${fmt(c1[0])} ${fmt(c1[1])} ${fmt(c2[0])} ${fmt(c2[1])} ${fmt(p2[0])} ${fmt(p2[1])}`;
  }
  return path;
}

/**
 * A loop drawn around something: it starts at a random angle, wanders a
 * little in radius and overshoots its start, so it never quite closes.
 */
export function roughEllipse(cx: number, cy: number, rx: number, ry: number, random: Random): string {
  const start = random() * Math.PI * 2;
  const sweep = Math.PI * 2 + 0.35 + random() * 0.3;
  const steps = 18;
  const points: Point[] = [];
  for (let step = 0; step <= steps; step += 1) {
    const angle = start + (sweep * step) / steps;
    // The loop drifts outward as it goes round, so the overlap sits apart.
    const drift = 1 + (step / steps) * 0.06 + jitter(random, 0.025);
    points.push([cx + Math.cos(angle) * rx * drift, cy + Math.sin(angle) * ry * drift]);
  }
  return smoothPath(points);
}

/** A slightly curved arrow with an open, two-stroke head. */
export function roughArrow(from: Point, to: Point, random: Random, curve = 0.18): string {
  const [x1, y1] = from;
  const [x2, y2] = to;
  const length = Math.hypot(x2 - x1, y2 - y1) || 1;
  const nx = -(y2 - y1) / length;
  const ny = (x2 - x1) / length;
  const mid: Point = [(x1 + x2) / 2 + nx * length * curve, (y1 + y2) / 2 + ny * length * curve];
  const shaft = smoothPath([from, mid, to]);
  // Head strokes follow the tangent at the tip (from the curve's midpoint).
  const angle = Math.atan2(y2 - mid[1], x2 - mid[0]);
  const head = 11;
  const wing = (side: number): Point => [
    x2 - Math.cos(angle + side * 0.45) * head,
    y2 - Math.sin(angle + side * 0.45) * head,
  ];
  return shaft + stroke(wing(1), to, random, 0.5) + stroke(wing(-1), to, random, 0.5);
}

/** A right-hand square bracket spanning a text block, drawn in a 24 × 100 box. */
export function roughBracket(random: Random): string {
  return (
    stroke([3, 2], [19, 3.5], random, 0.8) +
    stroke([19, 3], [18.5, 97], random, 0.35) +
    stroke([18.5, 96.5], [3, 98], random, 0.8)
  );
}

/** A quick underline, drawn left to right with a slight lift at the end. */
export function roughUnderline(width: number, random: Random): string {
  return stroke([2, 5], [width - 2, 3.5 + jitter(random, 1)], random, 0.6);
}

/** A box on a drawing, in its pixels from the top-left corner. */
export type Rect = { x: number; y: number; width: number; height: number };

/** A loop round a box, clear of it by `pad` across and down. */
export function loopAround(rect: Rect, random: Random, pad: readonly [number, number]): string {
  return roughEllipse(rect.x + rect.width / 2, rect.y + rect.height / 2, rect.width / 2 + pad[0], rect.height / 2 + pad[1], random);
}

/** A box drawn round a box, as round a line of a list, clear of it by `pad` across and down. */
export function boxAround(rect: Rect, random: Random, pad: readonly [number, number] = [4, 3]): string {
  return roughRect(rect.x - pad[0], rect.y - pad[1], rect.width + 2 * pad[0], rect.height + 2 * pad[1], random);
}

/**
 * A highlighter pass along a line of text: a band behind its letters whose edges wander a little
 * and whose ends are cut at the slant of a chisel tip, drawn as a closed shape to fill.
 */
export function highlighterBand(rect: Rect, random: Random): string {
  const top = rect.y + rect.height * 0.14, bottom = rect.y + rect.height * 0.94;
  const left = rect.x - 2 + jitter(random, 1), right = rect.x + rect.width + 2 + jitter(random, 1.5);
  const slant = 2 + random() * 2;
  const steps = Math.max(2, Math.round(rect.width / 28));
  const edge = (y: number, from: number, to: number): Point[] => Array.from({ length: steps + 1 },
    (_, step) => [from + ((to - from) * step) / steps, y + jitter(random, 0.9)]);
  const points = [...edge(top, left + slant, right), ...edge(bottom, right - slant, left)];
  return `M${points.map(([x, y]) => `${fmt(x)} ${fmt(y)}`).join("L")}Z`;
}
